import React, { createContext, useContext, useCallback, useEffect, useState, ReactNode } from 'react';
import { useColorScheme as useSystemColorScheme } from 'react-native';
import { ThemeManager, ThemePreference } from './ThemeManager';

interface ThemeContextValue {
  /**
   * Current theme preference (system/light/dark)
   */
  themePreference: ThemePreference;

  /**
   * Resolved color scheme ('light' or 'dark')
   */
  colorScheme: 'light' | 'dark';

  /**
   * Set theme preference
   */
  setThemePreference: (preference: ThemePreference) => Promise<void>;

  /**
   * Re-sync preference from ThemeManager (e.g. after scoped storage init)
   */
  refreshPreference: () => void;

  /**
   * Whether theme manager is initialized
   */
  isInitialized: boolean;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * ThemeProvider
 * Provides theme state and controls to the app.
 * Manages theme preference and resolves actual color scheme.
 */
export function ThemeProvider({ children }: ThemeProviderProps) {
  const systemColorScheme = useSystemColorScheme();
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>('system');
  const [isInitialized, setIsInitialized] = useState(false);
  const themeManager = ThemeManager.getInstance();

  // Initialize theme manager on mount
  useEffect(() => {
    async function initializeTheme() {
      await themeManager.initialize();
      const preference = themeManager.getPreference();
      setThemePreferenceState(preference);
      setIsInitialized(true);
    }

    initializeTheme();
  }, []);

  // Callback for StorageInitializer to notify when scoped storage is ready
  const refreshPreference = useCallback(() => {
    const preference = themeManager.getPreference();
    setThemePreferenceState(preference);
  }, [themeManager]);

  // Resolve color scheme based on preference and system theme
  const colorScheme: 'light' | 'dark' =
    themePreference === 'system'
      ? (systemColorScheme === 'dark' ? 'dark' : 'light')
      : themePreference;

  const setThemePreference = async (preference: ThemePreference) => {
    await themeManager.setPreference(preference);
    setThemePreferenceState(preference);
  };

  const value: ThemeContextValue = {
    themePreference,
    colorScheme,
    setThemePreference,
    refreshPreference,
    isInitialized,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Hook to access theme context
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
}
